import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CaptureLifecyclePanel } from '../../CaptureLifecyclePanel';
import { CaptureReviewsPanel } from '../../CaptureReviewsPanel';
import { GoNoGoPanel } from '../../GoNoGoPanel';
import { useGoNoGoRecords } from '../../goNoGoHooks';
import { useCaptureWorkspace } from '../../hooks';
import { useCaptureReviews } from '../../reviewHooks';
import { usePursuitRecord } from '../PursuitRecordContext';
import { PursuitSectionFrame } from '../PursuitSectionFrame';
/**
 * Go / no-go: gates are scheduled in CaptureReviewsPanel (GRW-05), so it comes
 * first under "Gates and reviews"; recorded decisions below; lifecycle last.
 * `?decisionId=` narrows the decisions to one record, as `overviewId` did on
 * the Overview tab.
 */ export function DecisionsSection() {
    const { captureProjectId, correlationId } = usePursuitRecord();
    const [gateToRecord, setGateToRecord] = useState();
    // Add flows open from here so, once there is data, "Add …" lives in the section header.
    const [decisionOpen, setDecisionOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    // GRW-30: sections always sit in the record shell, so the gate is the header capabilities.
    const { canEdit } = usePursuitRecord().capabilities;
    const { data } = useCaptureWorkspace(captureProjectId);
    const [params, setParams] = useSearchParams();
    const decisionId = params.get('decisionId');
    const hasReviews = (useCaptureReviews(captureProjectId).data ?? []).length > 0;
    const hasDecisions = (useGoNoGoRecords(captureProjectId).data ?? []).length > 0 || !!data?.goNoGoDecisions?.length;
    const all = data?.goNoGoDecisions ?? [];
    const decisions = decisionId ? all.filter((d)=>d.id === decisionId) : all;
    return /*#__PURE__*/ _jsxs(PursuitSectionFrame, {
        title: "Go / no-go",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                action: canEdit && hasReviews ? /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: ()=>setReviewOpen(true),
                    size: "sm",
                    "data-testid": Testids.pursuitDecisions.addReviewButton,
                    children: "Add review"
                }) : undefined,
                title: "Gates and reviews",
                variant: "group"
            }),
            /*#__PURE__*/ _jsx(CaptureReviewsPanel, {
                open: reviewOpen,
                onOpenChange: setReviewOpen,
                readOnly: !canEdit,
                captureProjectId: captureProjectId,
                onRecordDecision: (id)=>setGateToRecord({
                        id
                    })
            }),
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                action: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        decisionId && /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>{
                                const next = new URLSearchParams(params);
                                next.delete('decisionId');
                                setParams(next, {
                                    replace: true
                                });
                            },
                            size: "sm",
                            variant: "secondary",
                            children: "Show all decisions"
                        }),
                        canEdit && hasDecisions && /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>setDecisionOpen(true),
                            size: "sm",
                            "data-testid": Testids.pursuitDecisions.addButton,
                            children: "Add decision"
                        })
                    ]
                }),
                title: "Decisions",
                variant: "group"
            }),
            decisionId && decisions.length === 0 && /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "The selected decision record is unavailable."
            }),
            /*#__PURE__*/ _jsx("div", {
                id: "pursuit-decisions",
                children: /*#__PURE__*/ _jsx(GoNoGoPanel, {
                    open: decisionOpen,
                    onOpenChange: setDecisionOpen,
                    gateToRecord: gateToRecord,
                    readOnly: !canEdit,
                    captureProjectId: captureProjectId,
                    correlationId: correlationId,
                    decisions: decisions,
                    tasks: data?.tasks ?? []
                })
            }),
            data?.summary && /*#__PURE__*/ _jsx(CaptureLifecyclePanel, {
                readOnly: !canEdit,
                summary: data.summary,
                tasks: data.tasks ?? []
            }, data.summary.captureProjectId)
        ]
    });
}
